import React from "react";
import { z } from "zod";

import {
	FormCheckboxGroup,
	FormField,
	FormInputText,
	FormRadioButtons,
	PanelFilters,
} from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";

export const WORKFLOW_STATUS_OPTIONS = [
	{ label: "Any", value: "any" },
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "completed" },
	{ label: "Overdue", value: "overdue" },
];

const WorkflowsFiltersSchema = z.object({
	workflowName: z.string().default(""),
	subject: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
	status: z.enum(["any", "active", "completed", "overdue"]).default("any"),
	overdueOnly: z.array(z.string()).default([]),
	unassignedOnly: z.array(z.string()).default([]),
});

export type WorkflowsFilters = z.infer<typeof WorkflowsFiltersSchema>;

type WorkflowsFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<WorkflowsFilters>;
	onApplyFilters: (filters: Partial<WorkflowsFilters>) => void;
};

const WorkflowsFilterPanel = ({ isOpen, onClose, filters, onApplyFilters }: WorkflowsFilterPanelProps) => {
	const handleSubmit = ({ value }: { value: Partial<WorkflowsFilters> }) => {
		const formattedState = { ...value };

		// Remove unset fields so only applied filters remain (drives the chip list and badge count)
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (
				filterValue === null ||
				filterValue === undefined ||
				filterValue === "" ||
				(key === "status" && filterValue === "any") ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	const defaultValues: WorkflowsFilters = {
		workflowName: filters.workflowName || "",
		subject: filters.subject || null,
		status: filters.status || "any",
		overdueOnly: filters.overdueOnly || [],
		unassignedOnly: filters.unassignedOnly || [],
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={WorkflowsFiltersSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			headerWeight="bold"
		>
			<div className="space-y-4">
				<FormInputText id="workflowName" label="Workflow" placeholder="Search by workflow name" />

				<FormField<{ id: string; name: string } | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value}
							onChange={(employeeId, employeeName) =>
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								)
							}
							placeholder="Make a selection"
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={WORKFLOW_STATUS_OPTIONS} />

				<FormCheckboxGroup
					id="overdueOnly"
					options={[{ label: "Show only workflows with overdue to-dos", value: "true" }]}
				/>

				<FormCheckboxGroup
					id="unassignedOnly"
					options={[{ label: "Show only workflows with unassigned to-dos", value: "true" }]}
				/>
			</div>
		</PanelFilters>
	);
};

export default WorkflowsFilterPanel;
